import type { AnimatedComponent, UseTransitionProps } from 'react-spring'
import { animated, useTransition } from 'react-spring'
import { __DEV__ } from '@mote/helpers/constants'
import { x } from '@xstyled/styled-components'
import type { HTMLXStyledProps, XComponent } from '../../system'
import { forwardRef } from '../../system'
import { useDialogContext } from './DialogProvider'
import type { MotionPreset } from './types'

export interface DialogTransitionProps
  extends Omit<HTMLXStyledProps<'section'>, 'style'> {
  preset?: MotionPreset
  isOpen: boolean
}

const transitions: Record<MotionPreset, UseTransitionProps> = {
  slideInBottom: {
    from: { opacity: 0, translateY: 16 },
    enter: { opacity: 1, translateY: 0 },
    leave: { opacity: 0, translateY: 16 }
  },
  slideInRight: {
    from: { opacity: 0, translateX: 16 },
    enter: { opacity: 1, translateX: 0 },
    leave: { opacity: 0, translateX: 16 }
  },
  scale: {
    from: { opacity: 0, scale: 0.95 },
    enter: { opacity: 1, scale: 1 },
    leave: { opacity: 0, scale: 0.95 }
  },
  none: {}
}

const Motion = animated(x.section) as XComponent<'section'> &
  AnimatedComponent<'section'>

export const DialogTransition = forwardRef<DialogTransitionProps, 'section'>(
  (props, ref) => {
    const { preset = 'scale', isOpen, ...rest } = props
    const { setIsMounted } = useDialogContext()

    const motionProps = transitions[preset] as UseTransitionProps
    const transition = useTransition(isOpen, {
      ...motionProps,
      config: {
        duration: 150
      },
      onRest: (result: any, ctrl: any, item: boolean) => {
        if (!item) {
          setIsMounted(false)
        }
      }
    } as UseTransitionProps)

    return transition((style, item) =>
      item ? <Motion ref={ref} {...rest} style={style} /> : null
    )
  }
)

if (__DEV__) {
  DialogTransition.displayName = 'DialogTransition'
}
